import * as React from 'react';
import { AvailabilitySearchResultSeatingAreaDialog } from 'components/www/availability-search/AvailabilitySearchResultSeatingAreaDialog';
import { Button, Typography } from 'omakase';
import { DisplayMoney } from '@tock/money';
import { DisplayTime } from '@tock/time';
import { LocalDateTime } from 'js-joda';
import { LockOptions } from 'types';
import {
  button,
  priceTypography,
  simpleTimeItem,
} from 'components/www/availability-search/timeAndSizeItemCsssDefs';
import { consumer, core } from '@tock/coretypes';
import { getHasNonZeroEffectivePrice } from 'utils/priceDisplayUtils';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useState } from 'react';

interface TimeAndSizeItemSimpleProps {
  locale: string;

  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
  onClick(
    dateTime: LocalDateTime,
    priceCents: number,
    partySize: number,
    lockOptions?: Partial<LockOptions>
  ): void;
  partySize: number;
  price: number;
  minRequiredSpend: {
    totalCents: number;
    priceDisplayDistribution?: core.TicketPriceInformation.PriceDistribution;
  };
  priceDisplayStyle: consumer.OfferedPriceSummary.PriceDisplayStyle;
  time: string;
  timeZone: string;
  business: consumer.IBusinessConfig;
  offering: consumer.Offering;
  seatingAreas: core.SeatingArea[];
}

const TimeAndSizeItemSimple: React.FunctionComponent<TimeAndSizeItemSimpleProps> = ({
  minRequiredSpend: { totalCents: minRequiredSpendCents, priceDisplayDistribution },
  locale,
  lockedDateTime,
  lockedSeatingArea,
  onClick,
  partySize,
  price,
  priceDisplayStyle,
  time,
  timeZone,
  business,
  offering,
  seatingAreas,
}) => {
  const hasSeatingAreasConsumerSearch = useFeatureFlagForConsumer('SEATING_AREAS_CONSUMER_SEARCH');
  const [showChooseSeatingAreaModal, setShowChooseSeatingAreaModal] = useState(false);

  const handleSelectSeatingArea = (seatingAreaId: number) => {
    setShowChooseSeatingAreaModal(false);
    handleSelectDateAndTime(seatingAreaId);
  };

  const handleSelectDateAndTime = (seatingAreaId?: number) => {
    const lockOptions: Partial<LockOptions> = {};

    if (seatingAreaId) {
      lockOptions.seatingAreaId = seatingAreaId;
    }

    onClick(LocalDateTime.parse(time), price || 0, partySize, lockOptions);
  };

  const displayMinRequiredPrice = priceDisplayStyle === 'MIN_REQUIRED_SPEND';

  const priceDistribution =
    offering.price?.partyRangeConfigs?.find(
      (config) => price === config.ticketPriceInformation?.amountCents
    )?.ticketPriceInformation?.priceDistribution ?? 'PER_TICKET';

  const handleClick = () => {
    const hasSingleSeatingArea = seatingAreas.length === 1;
    const hasMultipleSeatingAreas = seatingAreas.length > 1;
    const isSeatingAreaOptionsFlagEnabled =
      hasSeatingAreasConsumerSearch && seatingAreas.length > 0;

    if (isSeatingAreaOptionsFlagEnabled) {
      if (hasSingleSeatingArea) {
        // Select single seating area on lock request
        handleSelectDateAndTime(seatingAreas[0].id);
      } else if (hasMultipleSeatingAreas) {
        setShowChooseSeatingAreaModal(true);
      }
    } else {
      handleSelectDateAndTime();
    }
  };

  const timeValue = LocalDateTime.parse(time).toLocalTime();
  const isLockedItem = !!lockedDateTime && !!time && time === lockedDateTime;
  const hasNonZeroEffectivePrice = getHasNonZeroEffectivePrice(
    price,
    minRequiredSpendCents,
    priceDisplayStyle
  );

  const renderPrice = hasNonZeroEffectivePrice;

  const purchaseSizeDisplay = (
    <span>
      {priceDistribution === 'PER_PARTY' || priceDisplayDistribution === 'PER_PARTY'
        ? ` per party`
        : ` × ${partySize}`}
    </span>
  );
  const hasSeatingAreasAvailable = hasSeatingAreasConsumerSearch && seatingAreas.length > 0;

  return (
    <div css={simpleTimeItem}>
      <Button
        css={button}
        size="small"
        variant={isLockedItem ? 'success' : 'primary'}
        data-testid="select-time"
        onClick={handleClick}
        aria-haspopup={hasSeatingAreasAvailable && 'dialog'}
        fullWidth
      >
        <DisplayTime localeFromProps={locale} zone={timeZone} timeValue={timeValue} />
      </Button>
      {renderPrice && (
        <Typography variant="caption" color="fgSubtle" css={priceTypography}>
          <DisplayMoney
            amount={displayMinRequiredPrice ? minRequiredSpendCents : price}
            roundIfWholeNumber={true}
          />
          {purchaseSizeDisplay}
        </Typography>
      )}
      {hasSeatingAreasAvailable && (
        <AvailabilitySearchResultSeatingAreaDialog
          business={business}
          dateTimeValue={LocalDateTime.parse(time)}
          isOpen={showChooseSeatingAreaModal}
          offering={offering}
          onClose={() => {
            setShowChooseSeatingAreaModal(false);
          }}
          onSelectSeatingArea={handleSelectSeatingArea}
          partySize={partySize}
          seatingAreas={seatingAreas}
          lockedSeatingArea={isLockedItem ? lockedSeatingArea : undefined}
        />
      )}
    </div>
  );
};

export default TimeAndSizeItemSimple;
